import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { loadAllShops, healthFor, colourForShop } from '../../lib/adminQueries';
import { AlertTriangle, Calendar, CreditCard } from 'lucide-react';

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const SIGNAL_INFO = {
  failed_payment:       { tone: 'r', label: 'Payment failed',          sub: 'Update card to keep service',          icon: <CreditCard size={16} /> },
  no_login_14d:         { tone: 'a', label: 'No login in 14+ days',    sub: 'Owner hasn\u2019t signed in recently',   icon: <Calendar size={16} /> },
  bookings_dropped_30:  { tone: 'a', label: 'Bookings down >30% w/w',  sub: 'Activity has dipped sharply',           icon: <AlertTriangle size={16} /> },
};

const ChurnInner = () => {
  const [loading, setLoading] = useState(true);
  const [bands, setBands]     = useState({ green: 0, amber: 0, red: 0 });
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const shops = await loadAllShops();
      const day = 86400000;
      const now = new Date();
      const sevenAgo = new Date(now.getTime() - 7 * day).toISOString();
      const fourteenAgo = new Date(now.getTime() - 14 * day).toISOString();

      // booking counts for last 7d and prior 7d
      const ids = shops.map(s => s.id);
      const counts = {};
      ids.forEach(id => { counts[id] = { thisWeek: 0, lastWeek: 0 }; });
      if (ids.length) {
        const { data: bks } = await supabase.from('bookings')
          .select('shop_id,starts_at,status')
          .in('shop_id', ids)
          .gte('starts_at', fourteenAgo);
        (bks || []).forEach(b => {
          if (b.status === 'cancelled') return;
          const c = counts[b.shop_id];
          const t = new Date(b.starts_at).getTime();
          if (t >= new Date(sevenAgo).getTime()) c.thisWeek += 1;
          else c.lastWeek += 1;
        });
      }

      // last login is on auth.users — RLS may not allow direct query for master_admin
      // by default. We'll skip the no_login_14d signal for now (computed only if we can read it).
      // Only shops with an active/trialing subscription are the "paying"
      // set. Percentages must be over this base — otherwise a platform
      // with 3 free shops shows "67% healthy of paying shops" (the bug
      // the master admin filed). If the paying set is empty we show a
      // no-data state below rather than divide by zero.
      const paying = shops.filter(sh => ['active', 'trialing'].includes(sh.subscription_status));
      const payingIds = new Set(paying.map(s => s.id));
      const tally = { green: 0, amber: 0, red: 0 };
      const ws = [];
      shops.forEach(sh => {
        const c = counts[sh.id] || { thisWeek: 0, lastWeek: 0 };
        const h = healthFor({
          subscriptionStatus: sh.subscription_status,
          lastLoginAt: null,
          bookingsThisWeek: c.thisWeek,
          bookingsLastWeek: c.lastWeek,
        });
        if (payingIds.has(sh.id)) tally[h.band] += 1;
        h.signals.forEach(sig => {
          ws.push({ shop: sh, signal: sig });
        });
      });
      setBands({ ...tally, payingTotal: paying.length });
      setWarnings(ws);
      setLoading(false);
    })();
  }, []);

  const total = bands.payingTotal || 0;
  const pct = (n) => total ? Math.round((n / total) * 100) : null;
  const pctLabel = (n) => {
    const p = pct(n);
    return p == null ? 'No paying shops yet' : `${p}% of paying shops`;
  };

  return (
    <main className="main admin-page" data-testid="admin-churn-page">
      <div className="topbar">
        <div>
          <h2>Churn watch</h2>
          <div className="sub">Green · amber · red early-warning system</div>
        </div>
      </div>

      <div className="body">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : (
          <>
            <div className="bands" data-testid="churn-bands">
              <div className="band g">
                <div className="top">
                  <span className="lbl"><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} /> Healthy</span>
                  <span className="big">{bands.green}</span>
                </div>
                <div className="rng">{pctLabel(bands.green)}</div>
                <div className="now">All metrics normal</div>
              </div>
              <div className="band a">
                <div className="top">
                  <span className="lbl"><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} /> At risk</span>
                  <span className="big">{bands.amber}</span>
                </div>
                <div className="rng">{total ? `${pct(bands.amber)}% — needs a nudge` : 'No paying shops yet'}</div>
                <div className="now">Login or activity dipped</div>
              </div>
              <div className="band r">
                <div className="top">
                  <span className="lbl"><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} /> Critical</span>
                  <span className="big">{bands.red}</span>
                </div>
                <div className="rng">{total ? `${pct(bands.red)}% — likely to churn` : 'No paying shops yet'}</div>
                <div className="now">Failed payment or major drop</div>
              </div>
            </div>

            <div className="panel" data-testid="churn-warnings">
              <h3 className="admin-info-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>At-risk shops</span>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', fontWeight: 'var(--fw-medium)', textTransform: 'none', letterSpacing: 0 }}>
                  {warnings.length} signal{warnings.length === 1 ? '' : 's'}
                </span>
              </h3>
              {warnings.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-base)' }}>
                  No warnings — every shop is healthy.
                </div>
              ) : warnings.map((w, i) => {
                const info = SIGNAL_INFO[w.signal];
                return (
                  <Link to={`/admin/shops/${w.shop.id}`} key={i} className={`sig ${info.tone}`} data-testid={`warning-${i}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <span className="ic">{info.icon}</span>
                    <span className="tx">
                      <b>{info.label}</b>
                      <small>{w.shop.name} · {info.sub}</small>
                    </span>
                    <span className="sq" style={{ background: colourForShop(w.shop), width: 26, height: 26, borderRadius: 7, fontSize: 'var(--fs-xs)' }}>
                      {initials(w.shop.name).charAt(0)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

const Churn = () => <AdminShell><ChurnInner /></AdminShell>;
export default Churn;
